<template>
  <v-dialog content-class="compliance-details-popup"
            v-if="visiblePopup"
            :value="visiblePopup"
            scrollable
            @click:outside='onCloseClickButton'
            @keydown.esc="onCloseClickButton">
    <v-card class="popup-wrapper">
      <div class="popup-container">
        <div class="main-part">
          <div class="header-line">
            <div class="compliance-title">
              {{detailsFormattedName}}
            </div>
            <div class="close"
                 @click="onCloseClickButton">
              X {{$t('newProject.esc')}}
            </div>
          </div>

          <div class="data-row">
            <div class="data-column-service-name">
              <div class="data-title">{{$t('cloud.compliance.popup.service.name')}}</div>
              <span class="date-column">
                {{details.ServiceName}}
              </span>
            </div>
            <div class="data-column">
              <div class="data-title">{{$t('cloud.compliance.popup.provider')}}</div>
              <span class="date-column" :class='providerCapitalize(details.Provider)'>
                {{details.Provider}}
              </span>
            </div>
            <div class="data-column">
              <div class="data-title">{{$t('cloud.compliance.popup.region')}}</div>
              <span class="date-column">
                {{details.Region}}
              </span>
            </div>
            <div class="data-column">
              <div class="data-title" v-if='details && details.Severity'>{{$t('cloud.compliance.popup.severity')}}</div>
              <issue-severity-chip v-if='details && details.Severity' class="severity-line" :issue='detailsSeverity'/>
            </div>
            <div class="data-column issue-status" v-if='details && details.Status'>
              <div class="data-title">{{$t('cloud.compliance.popup.status')}}</div>
              <span class="status-column" :style="{'color': getStatusColor}">
                {{ getStatus }}
              </span>
            </div>
            <div class="data-column-date">
              <div class="data-title">{{$t('cloud.compliance.popup.date')}}</div>
              <span class="date-column">
                {{detailsDateFormatted}}
              </span>
            </div>
          </div>
        </div>
        <div class='rest-part' :class="getWhiteLabeling">
          <div class='details-body-section'>
            <div class="data-block">
              <div class='details-column' v-if='details.CheckType'>
                <div class="data-title">{{$t('cloud.compliance.popup.check.type')}}</div>
                <div class="text-container">
                  {{details.CheckType}}
                </div>
              </div>
              <div class='details-column' v-if='details.AccountId'>
                <div class="data-title">{{$t('cloud.compliance.popup.account.id')}}</div>
                <div class="text-container">
                  {{details.AccountId}}
                </div>
              </div>
            </div>

            <div class="data-block" v-if='servicesDescriptionPopupResourceItems.length > 0'>
              <div class="data-title">{{$t('servicesDescriptionPopupTable.resource')}}</div>
              <div class="cloud-services-asset-details-popup-table">
                <wombat-table
                  :items="servicesDescriptionPopupResourceItemsPaginated"
                  :columns="tableColumnsServicesDescription"
                  :name='servicesDescriptionPopupName'
                  :resize="false"
                  :loadingItems="true"
                  :columnsReorder="false"
                  :itemHeight="58"
                  :noResultsMessage="$t('noResultsFound')"
                  class='details-popup-table'
                >
                  <component
                    slot="row-cell"
                    slot-scope="rowCell"
                    class="row-cell"
                    :is="tableRowCellComponentsHashServicesDescriptionPopup[rowCell.column.name] || tableRowCellComponentsHashServicesDescriptionPopup.default"
                    :item="rowCell.item"
                    :column="rowCell.column"
                  />
                </wombat-table>
                <span class='hide-resource-btn' @click.stop="isShowAllResource = !isShowAllResource" v-if='servicesDescriptionPopupResourceItems.length > 5'>
                {{ captionOfResourceToggleButton }}
                </span>
              </div>
            </div>
            <div class="data-block" v-if='details.Description'>
              <div class="data-title">{{$t('cloud.compliance.popup.description')}}</div>
              <div class="text-container">
                {{details.Description}}
              </div>
            </div>
            <div class="data-block" v-if='details.Risk'>
              <div class="data-title">{{$t('cloud.compliance.popup.risk')}}</div>
              <div class="text-container">
                {{details.Risk}}
              </div>
            </div>
            <div class="data-block" v-if='details.StatusExtended'>
              <div class="data-title">{{$t('cloud.compliance.popup.extended.status')}}</div>
              <div class="text-container">
                {{details.StatusExtended}}
              </div>
            </div>
          </div>

          <div class="remediation-block">
            <div class="data-title">{{$t('cloud.compliance.popup.remediation')}}</div>
            <div class='data-block' v-if='details.Remediation.Code.NativeIaC'>
              <div class="data-sub-title">{{$t('cloud.compliance.popup.native.iac')}}</div>
              <div class="text-container" v-if='isLink(details.Remediation.Code.NativeIaC)'>
                <a :href='details.Remediation.Code.NativeIaC' target="_blank" >
                  {{details.Remediation.Code.NativeIaC}}
                </a>
              </div>
              <div class="text-container" v-else>
                {{details.Remediation.Code.NativeIaC}}
              </div>
            </div>
            <div class='data-block' v-if='details.Remediation.Code.Terraform'>
              <div class="data-sub-title">{{$t('cloud.compliance.popup.terraform')}}</div>
              <div class="text-container" v-if='isLink(details.Remediation.Code.Terraform)'>
                <a :href='details.Remediation.Code.Terraform' target="_blank" >
                  {{details.Remediation.Code.Terraform}}
                </a>
              </div>
              <div class="text-container" v-else>
                {{details.Remediation.Code.Terraform}}
              </div>
            </div>
            <div class='data-block' v-if='details.Remediation.Code.CLI'>
              <div class="data-sub-title">{{$t('cloud.compliance.popup.cli')}}</div>
              <div class="text-container" v-if='isLink(details.Remediation.Code.CLI)'>
                <a :href='details.Remediation.Code.CLI' target="_blank" >
                  {{details.Remediation.Code.CLI}}
                </a>
              </div>
              <div class="text-container" v-else>
                {{details.Remediation.Code.CLI}}
              </div>
            </div>
            <div class='data-block' v-if='details.Remediation.Code.Other'>
              <div class="data-sub-title">{{$t('cloud.compliance.popup.additional.data')}}</div>
              <div class="text-container" v-if='isLink(details.Remediation.Code.Other)'>
                <a :href='details.Remediation.Code.Other' target="_blank" >
                  {{details.Remediation.Code.Other}}
                </a>
              </div>
              <div class="text-container" v-else>
                {{details.Remediation.Code.Other}}
              </div>
            </div>
            <div class='data-block' v-if='details.Remediation.Recommendation.Text'>
              <div class="data-sub-title">{{$t('cloud.compliance.popup.recommendation')}}</div>
              <div class="text-container">
                {{details.Remediation.Recommendation.Text}}
              </div>
            </div>
            <div class='data-block' v-if='details.Remediation.Recommendation.Url'>
              <div class="data-sub-title">{{$t('cloud.compliance.popup.url')}}</div>
              <div class="text-container" v-if='isLink(details.Remediation.Recommendation.Url)'>
                <a :href='details.Remediation.Recommendation.Url' target="_blank" >
                  {{details.Remediation.Recommendation.Url}}
                </a>
              </div>
              <div class="text-container" v-else>
                {{details.Remediation.Recommendation.Url}}
              </div>
            </div>
          </div>

          <div class="compliance-block" v-if='Object.keys(details.Compliance).length > 0'>
            <div class="data-title">{{$t('cloud.compliance.popup.compliance')}}</div>
            <div class="compliance-data" v-for='(type, index) in complianceTypes' :key='index'>
              <div class="data-sub-title">{{type}}</div>
              <div class='version-section'>
                <v-chip-group column>
                  <v-chip x-small
                          label
                          outlined
                          v-for="(version, index) in details.Compliance[type]"
                          :key="`${version}-${index}`"
                          class='chip'
                  >
                    {{version.replaceAll("_", ".")}}
                  </v-chip>
                </v-chip-group>
              </div>
            </div>
            <span class='hide-compliance-btn' @click.stop="isShowAllCompliance = !isShowAllCompliance" v-if='Object.keys(details.Compliance).length > 2'>
                {{ captionOfComplianceToggleButton }}
            </span>
          </div>
        </div>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters } from 'vuex';
import moment from 'moment';
import IssueSeverityChip from '@/components/common/IssueSeverityChip.vue';
import { CONFIGURATION, FORTH_LEVEL } from '@/constants/configuration';
import { CLEAR_CLOUD_SERVICES_LEVEL_ITEMS } from '@/store/project/actionTypes';
import ResourceArnTableCell from '@/components/tableCells/cloudServicesPopupTableCells/ResourceArnTableCell.vue';
import ResourceIdTableCell from '@/components/tableCells/cloudServicesPopupTableCells/ResourceIdTableCell.vue';
import ResourceTypeTableCell from '@/components/tableCells/cloudServicesPopupTableCells/ResourceTypeTableCell.vue';
import WombatTable from '@/components/WombatTable/WombatTable.vue';
import WombatRowDefaultCell from '@/components/WombatTable/WombatRowDefaultCell.vue';
import {
  servicesDescriptionPopupTableColumns,
} from '@/services/tableColumns';
import disableOuterScrollOnPopupVisible from '@/mixins/disableOuterScrollOnPopupVisible';

const DETAILS_DATE_FORMAT = 'DD MMM YYYY HH:mm';

export default {
  name: 'CloudComplianceDetailsPopup',
  mixins: [disableOuterScrollOnPopupVisible],
  components: {
    IssueSeverityChip,
    WombatTable,
    WombatRowDefaultCell,
  },
  props: {
    visiblePopup: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      isShowAllCompliance: false,
      isShowAllResource: false,
    };
  },
  computed: {
    ...mapGetters(['cloudServicesFourthLevelItems', 'cloudServicesThirdLevelItems', 'whiteLabelingConfig']),
    servicesDescriptionPopupResourceItems() {
      let resources = [];
      this.cloudServicesThirdLevelItems.forEach(item => {
        if (item.tld === this.details.tld && item.region === this.details.Region && item.serviceName === this.details.ServiceName &&
          item.checkType === this.details.CheckType && item.status === this.details.Status && item.description === this.details.Description) {
          resources = item.resources;
        }
      });
      return resources.map((item, index) => ({
        ...item,
        id: index + 1,
      }));
    },
    servicesDescriptionPopupResourceItemsPaginated() {
      let formattedItems = this.servicesDescriptionPopupResourceItems;
      if (!this.isShowAllResource) {
        formattedItems = formattedItems.slice(0, 4);
      }
      return formattedItems;
    },
    servicesDescriptionPopupName() {
      return 'services-description-popup-table';
    },
    tableRowCellComponentsHashServicesDescriptionPopup() {
      return {
        resourceType: ResourceTypeTableCell,
        resourceId: ResourceIdTableCell,
        resourceArn: ResourceArnTableCell,
        default: WombatRowDefaultCell,
      };
    },
    tableColumnsServicesDescription() {
      const columns = [
        servicesDescriptionPopupTableColumns.resourceType,
        servicesDescriptionPopupTableColumns.resourceId,
        servicesDescriptionPopupTableColumns.resourceArn,
      ].filter(column => column)
        .map(column => ({
          ...column,
          title: column.title && this.$t(column.title),
        }));
      return [...columns];
    },
    details() {
      return this.cloudServicesFourthLevelItems;
    },
    complianceTypes() {
      if (this.details?.Compliance && Object.keys(this.details?.Compliance).length > 0) {
        if (this.isShowAllCompliance) {
          return Object.keys(this.details.Compliance);
        }
        return Object.keys(this.details.Compliance).slice(0, 2);
      }
      return [];
    },
    getStatus() {
      return this.details?.Status || '';
    },
    getStatusColor() {
      switch (this.getStatus.toLowerCase()) {
      case 'fail':
        return 'var(--severity-critical)';
      case 'info':
        return 'var(--severity-medium)';
      case 'pass':
        return 'var(--severity-low)';
      default:
        return 'var(--text-primary)';
      }
    },
    captionOfComplianceToggleButton() {
      return this.isShowAllCompliance ? this.$t('detailed.issue.show.less.list') : this.$t('detailed.issue.show.more.list');
    },
    captionOfResourceToggleButton() {
      return this.isShowAllResource ? this.$t('detailed.issue.show.less.list') : this.$t('detailed.issue.show.more.list');
    },
    detailsDateFormatted() {
      return moment(this.details.AssessmentStartTime).format(DETAILS_DATE_FORMAT);
    },
    detailsFormattedName() {
      return this.details?.CheckTitle || '';
    },
    detailsSeverity() {
      return {
        severity: this.details?.Severity || 'Low',
      };
    },
    getWhiteLabeling() {
      return this.whiteLabelingConfig?.domain?.replace(/[-.]/g, '_') || 'cynergy_app';
    },
  },
  methods: {
    isLink(data) {
      return data.startsWith('https://');
    },
    providerCapitalize(provider) {
      if (provider === CONFIGURATION.SOURCE_TYPE.azure) {
        return 'title';
      }
      return 'upper';
    },
    async onCloseClickButton() {
      this.$emit('closed', true);
      await this.$store.dispatch(CLEAR_CLOUD_SERVICES_LEVEL_ITEMS, {layer: FORTH_LEVEL});
    },
  },
};
</script>
<style lang="scss">
@import '~@/assets/styles/design-system';

.v-dialog.compliance-details-popup.compliance-details-popup {
  max-width: rem(810);
  overflow-y: hidden;
}

.v-slide-group__wrapper.v-slide-group__wrapper {
  background: transparent;
}

// webkit scrollbar pseudo-elements can't match scoped selectors, unscoped .rest-part needed
.rest-part {
  scrollbar-color: var(--scrollbar-thumb) var(--scrollbar-track);
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: var(--scrollbar-track); }
  &::-webkit-scrollbar-thumb { background: var(--scrollbar-thumb); border-radius: 4px; }
}
</style>

<style lang="scss" scoped>
@import '~@/assets/styles/design-system';

.popup-wrapper {
  background: var(--bg-secondary);
  overflow-y: hidden;
}

.popup-container {
  min-height: rem(447);
  max-height: rem(887);
  border-radius: $radius-md;
  background-color: var(--bg-primary);
  box-shadow: $shadow-lg;
  position: relative;
  margin: auto;
  width: 100%;

  .close {
    cursor: pointer;
    display: inline-block;
    position: absolute;
    right: $space-2;
    top: $space-4;
    font-size: $font-size-sm;
    font-weight: 500;
    letter-spacing: 1.07px;
    line-height: $space-4;
    color: var(--brand-secondary);
  }

  .main-part {
    padding-left: $space-16;
    padding-top: $space-6;
    border-radius: $radius-md $radius-md 0 0;
    background-color: var(--bg-secondary);
    box-shadow: $shadow-xs;
  }

  .rest-part {
    max-height: rem(610);
    overflow-y: auto;
    scrollbar-width: thin;
    padding-left: $space-16;
    padding-top: $space-8;
    padding-bottom: $space-16;
    background-size: contain;
    background-position: right;
  }

  .cynergy_app {
    background-image: url("../../assets/icons/whitelabeling/cynergy.app/cynergy_app_half.svg");
  }

  .xmcyber_com {
    background-image: url("../../assets/icons/whitelabeling/xmcyber.com/xmcyber_com_half.svg");
  }

  .xmcyber_master_com {
    background-image: url("../../assets/icons/whitelabeling/xmcyber.com/xmcyber_com_half.svg");
  }

  .compliance-title {
    display: inline-block;
    text-transform: capitalize;
    font-family: $default-application-font-medium;
    height: $space-8;
    color: var(--text-primary);
    font-size: $font-size-xl;
    font-weight: bold;
    letter-spacing: 0;
    line-height: $space-7;
    width: rem(550);
  }

  .data-title {
    color: var(--text-secondary);
    font-size: $font-size-sm;
    letter-spacing: 0.8px;
    line-height: $space-2;
    text-transform: uppercase;
    margin-bottom: $space-1-5;
  }

  .severity-line {
    display: inline-block;
  }

  .date-column {
    color: var(--text-secondary);
    font-size: $font-size-base;
    letter-spacing: 0;
    line-height: $font-size-base;

    &.title {
      text-transform: capitalize;
      font-weight: normal;
    }

    &.upper {
      text-transform: uppercase;
    }
  }

  .status-column {
    font-size: $font-size-base;
    letter-spacing: 0;
    line-height: $font-size-base;
    font-weight: bold;
  }

  .data-block {
    display: block;

    .text-container::first-letter {
      text-transform: capitalize;
    }

    .text-container {
      padding-right: $space-16;
      font-size: $font-size-base;
      letter-spacing: 0;
      line-height: $space-4;
      padding-bottom: $space-9;
    }
  }

  .compliance-block {
    padding-bottom: $space-8;
    display: block;

    .version-section {
      max-width: rem(750);

      .v-chip {
        pointer-events: none;
        color: var(--text-primary) !important;

        &:hover {
          cursor: default;
        }
      }
    }

    .compliance-data {
      padding-top: $space-4;
    }
  }

  .data-sub-title {
    font-size: $font-size-xs;
    color: var(--text-secondary);
    letter-spacing: 0.8px;
    line-height: $space-2;
    text-transform: uppercase;
    margin-bottom: $space-1-5;
  }

  .data-row {
    margin-top: $space-6;
    padding-bottom: $space-9;
    display: flex;
    flex-direction: row;

    .details-column {
      display: inline-block;
      width: fit-content;
      max-width: rem(420);
      margin-right: $space-4;
    }

    .data-column {
      display: inline-block;
      min-width: rem(90);
      max-width: rem(120);
      margin-right: $space-4;
    }

    .data-column-date {
      display: inline-block;
      width: rem(135);
      margin-right: $space-4;
    }

    .data-column-service-name {
      display: inline-block;
      width: rem(130);
      margin-right: $space-4;
    }
  }

  .hide-compliance-btn,
  .hide-resource-btn {
    color: var(--brand-secondary);
    font-family: $default-application-font-regular;
    font-size: $font-size-base;
    letter-spacing: 0;

    &:hover {
      cursor: pointer;
    }
  }

  .hide-resource-btn {
    margin-left: rem(3);
  }

  .details-column {
    display: inline-block;
    width: fit-content;
    max-width: rem(420);
    margin-right: $space-4;
  }

  .cloud-services-asset-details-popup-table {
    width: 95%;
    margin-top: $space-5;
    margin-bottom: $space-5;

    .details-popup-table {
      margin-bottom: rem(5);
      min-height: rem(30);
    }
  }
}
</style>
